// Lead import screen. The file is parsed in the browser (never uploaded/stored):
// we send only the parsed rows to a preview that writes nothing, show what would be
// created, let the user resolve duplicates, and write only on confirm. Re-running the
// same file creates nothing (the server skips names that already exist).
import { useState } from 'react';
import * as XLSX from 'xlsx';
import { leadImportApi } from '../../api';
import he from '../../i18n/he';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import { Loader, ErrorText } from '../../components/Loader';

export default function LeadImport() {
  const [rows, setRows] = useState(null);
  const [preview, setPreview] = useState(null);
  const [exclude, setExclude] = useState(new Set()); // duplicate rows the user skips
  const [phase, setPhase] = useState('idle'); // idle | parsing | preview | done
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const reset = () => {
    setRows(null); setPreview(null); setExclude(new Set());
    setPhase('idle'); setResult(null); setError(null);
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError(null); setPhase('parsing');
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const parsed = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
      setRows(parsed);
      const p = await leadImportApi.preview(parsed);
      setPreview(p); setExclude(new Set()); setPhase('preview');
    } catch (err) {
      setError(err); setPhase('idle');
    }
  };

  const toggle = (index) => setExclude((prev) => {
    const next = new Set(prev);
    if (next.has(index)) next.delete(index); else next.add(index);
    return next;
  });

  const commit = async () => {
    setError(null);
    try {
      const res = await leadImportApi.commit(rows, [...exclude]);
      setResult(res); setPhase('done');
    } catch (err) {
      setError(err);
    }
  };

  return (
    <>
      <PageHeader title={he.leadImport.title}>
        {phase !== 'idle' && <Button variant="subtle" small onClick={reset}>{he.leadImport.reset}</Button>}
      </PageHeader>

      {error && <ErrorText error={error} />}

      {/* A real button over the native control, which is unstyled and unthemed. */}
      {phase === 'idle' && (
        <label className="btn file-pick">
          <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} />
          {he.leadImport.pick}
        </label>
      )}

      {phase === 'parsing' && <Loader />}

      {phase === 'preview' && preview && (
        <>
          {/* What will happen, then how the sheet's columns were read — plain lines, no box. */}
          <div className="summary-row import-summary">
            <span className="badge blue">{he.leadImport.willCreate}: {preview.createCount}</span>
            {preview.alreadyImportedCount > 0 && (
              <span className="owed-quiet">{he.leadImport.alreadyImported}: {preview.alreadyImportedCount}</span>
            )}
          </div>
          <p className="muted small-note import-mapping">
            {he.leadImport.mappingTitle}: {he.leadImport.nameCol} · {he.leadImport.contactCol} → {he.leadImport.referredByCol} · {he.leadImport.noteCol} → {he.tasks.title} · {he.leadImport.ownerCol}
          </p>

          {/* The rows that need a decision or will not import, as sections of one surface. */}
          {(preview.duplicates.length > 0 || preview.unparseable.length > 0
            || (preview.noNextStep && preview.noNextStep.length > 0)) && (
            <div className="card import-review">
              {preview.duplicates.length > 0 && (
                <section>
                  <h3>{he.leadImport.duplicatesTitle}</h3>
                  {preview.duplicates.map((d) => (
                    <div key={d.name} className="dup-group">
                      <strong>{d.name}</strong>
                      {d.indices.map((idx) => (
                        <label key={idx} className="dup-row">
                          <input type="checkbox" checked={!exclude.has(idx)} onChange={() => toggle(idx)} />
                          <span>{he.leadImport.include} ({he.leadImport.nameCol} #{idx + 1})</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </section>
              )}

              {preview.unparseable.length > 0 && (
                <section>
                  <h3>{he.leadImport.unparsableTitle}</h3>
                  <ul className="muted small-note">
                    {preview.unparseable.map((u) => (
                      <li key={u.index}>#{u.index + 1}: {u.raw.join(' | ') || '—'}</li>
                    ))}
                  </ul>
                </section>
              )}

              {preview.noNextStep && preview.noNextStep.length > 0 && (
                <section>
                  <h3>{he.leadImport.noNextStepTitle} <span className="muted">({preview.noNextStep.length})</span></h3>
                  <ul className="muted small-note">
                    {preview.noNextStep.map((l) => <li key={l.name}>{l.name}</li>)}
                  </ul>
                </section>
              )}
            </div>
          )}

          <div className="card">
            <table className="lead-preview">
              <thead>
                <tr>
                  <th>{he.leadImport.nameCol}</th>
                  <th>{he.leadImport.contactCol}</th>
                  <th>{he.leadImport.ownerCol}</th>
                  <th>{he.leadImport.noteCol}</th>
                </tr>
              </thead>
              <tbody>
                {preview.leads.map((l) => (
                  <tr key={l.index} className={l.alreadyExists || exclude.has(l.index) ? 'muted' : ''}>
                    <td>
                      {l.name}
                      {l.alreadyExists && <span className="badge amber"> {he.leadImport.alreadyImported}</span>}
                    </td>
                    <td>{l.referred_by ? `${he.leads.referredBy}: ${l.referred_by}` : (l.contact_name || '—')}</td>
                    <td>{l.owner_name || <span className="muted">{he.leadImport.unmatchedOwner}</span>}</td>
                    <td>{l.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="actions">
            <Button onClick={commit}>{he.leadImport.doImport}</Button>
            <Button variant="subtle" onClick={reset}>{he.common.cancel}</Button>
          </div>
        </>
      )}

      {phase === 'done' && result && (
        <div className="card summary-row">
          <span className="badge green">{he.leadImport.committed}: {result.created}</span>
          <span className="owed-quiet">{he.leadImport.alreadyImported}: {result.skipped}</span>
          <Button small variant="subtle" onClick={reset}>{he.leadImport.reset}</Button>
        </div>
      )}
    </>
  );
}
